import {
  Badge,
  Box,
  Button,
  HStack,
  Text
} from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { Link } from "react-router-dom";

interface ScriptTemplate {
  id: string;
  name: string;
  description: string;
  placement: "head" | "body";
  type: "script" | "meta" | "mixed";
  status: "active" | "inactive";
  usageCount: number;
  lastModified: string;
  createdBy: string;
  paths?: string[];
}

interface ScriptsTableProps {
  scripts: ScriptTemplate[];
  onSelectionChanged: (selectedScripts: ScriptTemplate[]) => void;
  onToggleStatus: (id: string, currentStatus: string) => void;
  loadingScripts: Set<string>;
}

const ScriptsTable = forwardRef<AgGridReact, ScriptsTableProps>(
  ({ scripts, onSelectionChanged: _onSelectionChanged, onToggleStatus, loadingScripts }, ref) => {
    const gridRef = useRef<AgGridReact>(null);

    useImperativeHandle(ref, () => gridRef.current as AgGridReact);

    return (
      <Box
        bg="white"
        rounded="2xl"
        shadow="xl"
        overflow="hidden"
        border="1px"
        borderColor="gray.200"
      >
        {/* Header with info */}
        <Box p={4} borderBottom="1px" borderColor="gray.200">
          <HStack justify="space-between">
            <Text fontWeight="semibold">Script Templates</Text>
            <Text fontSize="sm" color="gray.600">
              {scripts.length} templates configured
            </Text>
          </HStack>
        </Box>

        {/* Simple display without complex cellRenderers */}
        {scripts.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text color="gray.500">No script templates configured yet.</Text>
          </Box>
        ) : (
          <Box>
            {scripts.map((script) => (
              <Box
                key={script.id}
                p={4}
                borderBottom="1px"
                borderColor="gray.100"
                _hover={{ bg: "gray.50" }}
              >
                <HStack justify="space-between">
                  <Box flex="1">
                    <HStack gap={4} mb={2}>
                      <Link to={`/website/script/${script.id}`}>
                        <Text
                          fontWeight="medium"
                          color="blue.600"
                          _hover={{ textDecoration: "underline" }}
                        >
                          {script.name}
                        </Text>
                      </Link>
                      <Badge
                        colorScheme={script.placement === "head" ? "orange" : "blue"}
                        variant="subtle"
                      >
                        {script.placement.toUpperCase()}
                      </Badge>
                      <Badge
                        colorScheme={
                          script.type === "script" ? "blue" : 
                          script.type === "meta" ? "purple" : "green"
                        }
                        variant="subtle"
                      >
                        {script.type.charAt(0).toUpperCase() + script.type.slice(1)}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {script.description}
                    </Text>
                    <HStack gap={4} fontSize="sm" color="gray.500">
                      <Text>
                        {script.usageCount} {script.usageCount === 1 ? "path" : "paths"}
                      </Text>
                      <Text>•</Text>
                      <Text>Modified {script.lastModified}</Text>
                      <Text>•</Text>
                      <Text>By {script.createdBy}</Text>
                    </HStack>
                  </Box>
                  <HStack gap={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme={script.status === "active" ? "green" : "gray"}
                      onClick={() => onToggleStatus(script.id, script.status)}
                      loading={loadingScripts.has(script.id)}
                    >
                      {script.status === "active" ? "Active" : "Inactive"}
                    </Button>
                    <Link to={`/website/script/${script.id}`}>
                      <Button size="sm" variant="outline">
                        Edit
                      </Button>
                    </Link>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }
);

ScriptsTable.displayName = "ScriptsTable";

export default ScriptsTable;